/**
 * EdgeDeployments feature — form validation, UI state and imported HTTP contracts.
 *
 * The surface is the tenant admin's half of the on-premises enforcement tier: review a host's
 * enrolment request, activate or suspend an edge, confirm an instance's evidence-signing key, and read which
 * door serves this tenant right now. Every shape below is transcribed from the Go handler
 * (backend/internal/edge/edgeenrollment/admin_handler.go), not guessed:
 *
 *   GET    /edge-deployments                                     → { edges: [{edge_id,status,revoked,enforcing,role}] }
 *   POST   /edge-deployments/{id}/activate | /suspend            → 200 grantMoveResponse (NOT 204, deliberately:
 *                                                          a no-content answer to "suspend" is read as
 *                                                          "done, my traffic is back", and it is not)
 *   GET    /edge-deployments/{id}/evidence-keys                  → { evidence_keys: [evidenceKeyResponse] }
 *   POST   /edge-deployments/{id}/evidence-keys/{tp}/trust       → 204   (body: { note })
 *   POST   /edge-deployments/{id}/evidence-keys/{tp}/untrust     → 204
 *
 * WHAT THE API DOES NOT RETURN, recorded here rather than inferred (see the mock's "Recorded
 * divergence" note in docs/security.md):
 *   - `operator` (provider | customer). The column exists on the edge table and governs who may
 *     confirm an evidence key, but the tenant roster does not carry it, so this surface does not
 *     claim who runs an edge. A tenant learns it today only from the 409 the trust verb returns.
 * WHAT THIS LIST USED TO CONTAIN, AND WHY IT MATTERS THAT IT NO LONGER DOES. A trust PROVENANCE
 * discriminator was listed above as missing, on the reasoning that "the announce-with-attestation
 * path is ruled but not built, so every trusted row the API returns today was confirmed by a
 * person, and that is what the surface says". The path WAS built and wired, and attestation is on
 * by chart default — so this surface printed "A person confirmed it" directly above the API's own
 * note, "no person confirmed this fingerprint". `vouched_by` LANDED 2026-08-24 and
 * `evidenceKeyTrustState` reads it. The general rule the episode bought: a note recording what an
 * API does not return has to be re-checked against the API, not inherited.
 */

import { z } from "zod";
import { admitsPeople } from "./use-cases";
import { edgeAccessOptionsResponseSchema } from "@/generated/edge-approval-contract";
export type EdgeAccessOption = z.infer<typeof edgeAccessOptionsResponseSchema>["options"][number];

import { edgeRetirementResponseSchema } from "@/generated/edge-retirement-contract";

export type EdgeRetirement = NonNullable<z.infer<typeof edgeRetirementResponseSchema>["retirement"]>;
import { edgeRegistrationProposalSchema, edgeApprovalSchema, edgeImageUpdateApprovalSchema, edgeCombinedApprovalSchema, edgeRegistrationSchema, edgeSiteSchema, relayAdmissionPolicySchema, servingCertificateSchema, servingCertificateListSchema, servingCertificateRevocationSchema, issuanceObservationSchema, outsideCertificateSchema, edgeContainmentReceiptSchema } from "@/generated/edge-approval-contract";
export { edgeRegistrationProposalSchema, edgeApprovalSchema, edgeRegistrationSchema, edgeSiteSchema, relayAdmissionPolicySchema, servingCertificateSchema, servingCertificateListSchema, servingCertificateRevocationSchema, issuanceObservationSchema, outsideCertificateSchema, edgeContainmentReceiptSchema };
export type RelayAdmissionPolicy = z.infer<typeof relayAdmissionPolicySchema>;
export type ServingCertificate = z.infer<typeof servingCertificateSchema>;
export type ServingCertificateList = z.infer<typeof servingCertificateListSchema>;
export type IssuanceObservation = z.infer<typeof issuanceObservationSchema>;
export type OutsideCertificate = z.infer<typeof outsideCertificateSchema>;
export type ServingCertificateRevocation = z.infer<typeof servingCertificateRevocationSchema>;
export type EdgeContainmentReceipt = z.infer<typeof edgeContainmentReceiptSchema>;
/** Why a public serving certificate is revoked, as the API names it, with the words a person reads. */
export const revocationReasons = [
  { value: "key_compromise", label: "Its key may have left the Edge", hint: "The key is never certified again." },
  { value: "superseded", label: "It is being replaced", hint: "A fresh key and certificate take its place." },
  { value: "cessation_of_operation", label: "The application is going away", hint: "Nothing will serve this name." },
] as const;
export type RevocationReason = (typeof revocationReasons)[number]["value"];
export type RelayAdmissionMode = RelayAdmissionPolicy["mode"];

/**
 * The reachability form. Ranges are typed one per line; the server holds the exact rules (no
 * broader than /16 or /40, at most 64) and its refusal is shown as written.
 */
export const relayAdmissionFormSchema = z
  .object({
    mode: z.enum(["portal_launched", "declared_egress", "any_source"]),
    ranges: z.string(),
  })
  .superRefine((value, ctx) => {
    const ranges = splitRanges(value.ranges);
    if (value.mode === "declared_egress" && ranges.length === 0) {
      ctx.addIssue({ code: "custom", path: ["ranges"], message: "Add at least one egress range." });
    }
    const malformed = ranges.find((range) => !/^[0-9a-fA-F:.]+\/\d{1,3}$/.test(range));
    if (value.mode === "declared_egress" && malformed) {
      ctx.addIssue({ code: "custom", path: ["ranges"], message: `${malformed} is not a range such as 203.0.113.0/24.` });
    }
  });
export type RelayAdmissionForm = z.infer<typeof relayAdmissionFormSchema>;

export function splitRanges(text: string): string[] {
  return text.split(/[\s,]+/).map((range) => range.trim()).filter((range) => range.length > 0);
}
export type EdgeManifestApproval = z.infer<typeof edgeApprovalSchema> | z.infer<typeof edgeImageUpdateApprovalSchema> | z.infer<typeof edgeCombinedApprovalSchema>;

// ── List / drill-in state, which lives in the URL ──────────────────────────

/**
 * Roster filter. `serving` is the derived answer an operator actually asks for, not a stored
 * status: an active grant on a cut-off edge enforces nothing.
 *
 * THERE IS NO `suspended` OPTION, AND THAT IS NOT AN OVERSIGHT — see `edgeState` below. Suspending
 * and never-having-been-activated are the same stored status on the wire, so a `suspended` filter
 * could only ever match nothing. It was offered for weeks and always returned an empty roster.
 */
export const edgeFilterSchema = z.enum(["all", "serving", "waiting", "cut_off", "retired"]);
export type EdgeDeploymentFilter = z.infer<typeof edgeFilterSchema>;

/**
 * The columns the enrolment queue may be ordered by.
 *
 * ORDERING IS A SECURITY CONTROL ON THIS SURFACE, not a convenience. The server answers
 * `ORDER BY requested_at DESC`, so anyone who can post a request can be row one by posting one
 * second later, and can push the machine the operator is actually waiting for below the fold by
 * posting twenty times. A column the reader can order by is what takes that choice back.
 */
export const sortOrderSchema = z.enum(["asc", "desc"]);
export type SortOrder = z.infer<typeof sortOrderSchema>;

export const edgeDeploymentsSearchSchema = z.object({
  /**
   * Roster filter. Optional so a bare /edge-deployments URL is canonical, and `.catch(undefined)`
   * so a link someone saved while `?status=suspended` was still offered degrades to "all" instead
   * of failing the route's search validation on arrival.
   */
  status: edgeFilterSchema.optional().catch(undefined),
  use_case: z.enum(["web", "privileged", "broker", "egress"]).optional().catch(undefined),
  registration: z.string().uuid().optional().catch(undefined),
  code: z.string().regex(/^[A-Z0-9-]{12,14}$/).optional().catch(undefined),
  /** The edge whose instance keys and doors are open. A drill-in that is a shareable link. */
  edge: z.string().optional(),
  section: z.enum(["overview", "access", "identity", "recovery"]).optional().catch(undefined),
});
export type EdgeDeploymentsSearch = z.infer<typeof edgeDeploymentsSearchSchema>;

// ── The roster ────────────────────────────────────────────────────────────

export const edgeSchema = z.object({
  edge_id: z.string(),
  deployment_id: z.string().optional(),
  deployment_edge_ids: z.array(z.string()).optional(),
  /** pending | active. Pending means enrolled and deciding nothing for this tenant. */
  status: z.string(),
  /**
   * Cut off. Containment is the TENANT's act, and so is restoring it: both verbs are mounted on
   * the tenant admin API behind `edge.manage`, step-up and an interactive session
   * (edgeenrollment/containment_handler.go, MountContainment). This comment used to say the
   * opposite, which was true only while `pureid-admin` on the provider's host was the sole caller.
   */
  revoked: z.boolean(),
  retirement_state: z.enum(["pending", "retired"]).optional(),
  retired: z.boolean().optional(),
  /**
   * DERIVED SERVER-SIDE and the fact that matters: active AND not revoked. The handler computes it
   * because reporting status alone reads as "your traffic is on the edge" for an edge that was
   * cut off.
   */
  enforcing: z.boolean(),
  /** Installation health is independent of the tenant's API traffic assignment. */
  installation: z.object({
    state: z.enum(["installing", "active", "revoked"]),
    connection: z.enum(["connecting", "connected", "offline", "revoked"]),
    last_connected_at: z.string().datetime().optional(),
    reason: z.string(),
  }).optional(),
  /**
   * What this group serves, declared when it enrolled: any of api, private, internet. An EMPTY
   * array is a real answer, not a gap — it means the group predates the door model and the plane
   * cannot say what it exposes. The instance mounts routes from its own PUREID_EDGE_DOORS, so this
   * is what was DECLARED rather than what is live.
   */
  role: z.string().default(""),
});
export type EdgeDeployment = z.infer<typeof edgeSchema>;

// A routine removal is a server-owned operation. The customer installer may delete a workload only
// after this record reaches removal_authorized; an elapsed delay or successful revoke is not a receipt.
export const edgeDecommissionStateSchema = z.enum([
  "requested",
  "approved",
  "containment_committed",
  "containment_observed",
  "route_committed",
  "route_observed",
  "removal_authorized",
  "failed_retryable",
  "customer_route_required",
  "cancelled",
  "superseded",
]);

export const edgeDecommissionSchema = z.object({
  operation_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  deployment_id: z.string(),
  edge_ids: z.array(z.string()).min(1),
  state: edgeDecommissionStateSchema,
  member_fingerprints: z.array(z.string()).min(1),
  route_owner: z.enum(["provider", "customer"]),
  route_origins: z.array(z.string().url()).min(1),
  expected_route_target: z.string().min(1),
  inventory_generation: z.number().int().positive(),
  containment_revision: z.number().int().nonnegative().optional(),
  expected_consumers: z.array(z.string()).optional(),
  route_evidence: z.unknown().optional(),
  requested_at: z.string(),
  approved_at: z.string().optional(),
  containment_committed_at: z.string().optional(),
  containment_observed_at: z.string().optional(),
  route_committed_at: z.string().optional(),
  route_observed_at: z.string().optional(),
  removal_authorized_at: z.string().optional(),
  cancelled_at: z.string().optional(),
  last_error: z.string().optional(),
});
export type EdgeDecommission = z.infer<typeof edgeDecommissionSchema>;

export const edgeDecommissionsResponseSchema = z.object({
  decommissions: z.array(edgeDecommissionSchema),
});

export const edgeDeploymentsResponseSchema = z.object({
  /**
   * `edges`, not `edgeDeployments`: this is the key the SERVER actually sends
   * (edgeenrollment/admin_handler.go). The console's own vocabulary is "edge deployment", but the
   * wrapper key on the wire is not, and renaming it here silently broke the roster once already —
   * zod parsed `undefined` and the whole page failed with a type error no gate could see.
   */
  edges: z.array(edgeSchema),
});
export type EdgeDeploymentsResponse = z.infer<typeof edgeDeploymentsResponseSchema>;

/** The three states a row can be in, in the vocabulary the surface speaks. */
export type EdgeDeploymentState = "serving" | "waiting" | "cut_off";

/**
 * API assignment comes from the server's serving-assignment observation, independently
 * of lifecycle status. An active Edge may be unassigned; status alone cannot establish
 * where this tenant's API calls are enforced. Revocation takes precedence in the roster.
 */
export function edgeState(edge: EdgeDeployment): EdgeDeploymentState {
  if (edge.revoked) return "cut_off";
  if (edge.role === "broker" && edge.enforcing) return "serving";
  return "waiting";
}

export function matchesFilter(edge: EdgeDeployment, filter: EdgeDeploymentFilter): boolean {
  if (filter === "retired") return isEdgeRetired(edge);
  if (isEdgeRetired(edge)) return false;
  return filter === "all" || edgeState(edge) === filter;
}

export function isEdgeRetired(edge: EdgeDeployment): boolean {
  return edge.retired === true || edge.retirement_state === "retired";
}

export function hasEdgeRetirement(edge: EdgeDeployment): boolean {
  return isEdgeRetired(edge) || edge.retirement_state === "pending";
}



// ── What activate / suspend actually did ──────────────────────────────────

/**
 * The answer the server returns to a lifecycle verb. It exists because a 204 to "suspend" reads as
 * "done, my traffic is back", and it is not: lifecycle status, assignment and routing are separate.
 *
 * Parsed leniently at the call site (safeParse). The verb is what matters and it has already taken
 * effect by the time this body is read, so a response the surface cannot parse must never present
 * as a failed act; the surface falls back to its own copy, which says the same thing.
 */
export const grantMoveResponseSchema = z.object({
  edge_id: z.string(),
  /** Whether this edge is now the tenant's enforcement point. */
  enforcing: z.boolean(),
  /** Always false, and stated rather than omitted. No verb on this surface touches routing. */
  traffic_moved: z.boolean(),
  detail: z.string(),
  /** The act that is still owed, in the server's own words. */
  next_step: z.string(),
});
export type GrantMoveResponse = z.infer<typeof grantMoveResponseSchema>;

// ── Instance evidence keys ────────────────────────────────────────────────

export const evidenceKeySchema = z.object({
  /** RFC 7638 thumbprint, 43 characters of unpadded base64url. The instance's identity. */
  thumbprint: z.string(),
  /** The edge has published this key's public half. Material, never trust. */
  announced: z.boolean(),
  /** This tenant has confirmed the fingerprint. Trust, never material. */
  trusted: z.boolean(),
  first_seen_at: z.string().optional(),
  last_seen_at: z.string().optional(),
  trusted_at: z.string().optional(),
  /** The operator's record of HOW the fingerprint was compared. The out-of-band step, written down. */
  note: z.string().optional(),
  /**
   * PureID confirmed this key on an edge THIS TENANT operates — break-glass, not delegation.
   *
   * Defaulted rather than optional: a control plane that does not send the field has not told us
   * "false", it has told us nothing, and the safe reading of nothing here is the ordinary case. The
   * backend sends it un-omitted on every trusted key precisely so this default is never load-bearing.
   */
  is_platform_override: z.boolean().default(false),
  /** What the platform admin wrote down. Refused under 20 characters by the database. */
  override_reason: z.string().optional(),
  /**
   * WHO confirmed it. Absent means nothing has. Never infer this — inferring it is the defect this
   * field exists to close.
   */
  vouched_by: z.enum(["human", "attestation"]).optional(),
  /** The binding that vouched, when `vouched_by` is `attestation`. Absent for a human confirmation. */
  vouched_issuer: z.string().optional(),
  vouched_subject: z.string().optional(),
  vouched_method: z.string().optional(),
  /**
   * WHOSE confirmation it is, and therefore who may withdraw it. A DIFFERENT AXIS from `vouched_by`,
   * which says who decided.
   *
   * `edge` means the row lives in the provider's edge-scope table: PureID confirmed it, once, for
   * EVERY tenant on that edge. This tenant can see it — it signs into their evidence chain — and
   * cannot withdraw it, because withdrawing would cut off tenants they do not run the edge for.
   *
   * Present only after confirmation. An announced, untrusted key has no confirmation scope yet.
   * Trusted keys must carry their scope; never infer tenant authority when it is missing.
   */
  scope: z.enum(["tenant", "edge"]).optional(),
}).refine((key) => !key.trusted || key.scope !== undefined, {
  message: "A trusted evidence key must identify its confirmation scope",
  path: ["scope"],
});
export type EvidenceKey = z.infer<typeof evidenceKeySchema>;

export const evidenceKeysResponseSchema = z.object({
  evidence_keys: z.array(evidenceKeySchema),
});
export type EvidenceKeysResponse = z.infer<typeof evidenceKeysResponseSchema>;

/**
 * The states in the full union the API deliberately returns, including the two one-sided ones.
 *
 * `confirmed_never_announced` is the typo case and is the reason the union is returned at all: a
 * mistyped fingerprint trusts nothing, admits no instance, and without showing it the surface would
 * report a confirmation that quietly does nothing.
 */
export type EvidenceKeyTrustState =
  | "person_confirmed"
  | "platform_override_confirmed"
  | "provider_confirmed"
  | "platform_vouched"
  | "confirmed_unattributed"
  | "announced_unconfirmed"
  | "confirmed_never_announced";

/**
 * `platform_vouched` is a SEPARATE state rather than a footnote on `person_confirmed`, because the
 * two answer different questions. A person compared a fingerprint out of band; a platform token
 * asserted a workload. The second is a weaker claim about a different thing, and a surface that
 * renders them alike tells an operator a machine-admitted instance was checked by a human.
 *
 * Provenance is READ, never derived. `vouched_by` absent on a trusted row means the API is older
 * than this field, and the honest rendering is `confirmed_unattributed` — "confirmed", with no
 * claim about by whom. That state exists precisely so the absent case has somewhere to go that is
 * not a guess: defaulting it to `person_confirmed` would reproduce the original defect on every
 * deployment running a control plane older than the field, which is the population least able to
 * notice.
 *
 * `provider_confirmed` is the same discipline applied to the second axis. On an edge PureID runs,
 * the person who compared the fingerprint was a PureID operator, not one of the tenant's — so a chip
 * reading "a person confirmed it" on this tenant's screen would name a comparison nobody in this
 * organisation made. Both terms are READ (`vouched_by` and `scope`); neither is derived from the
 * other, and a row that reports one and not the other falls back to the state that claims less.
 */
export function evidenceKeyTrustState(key: EvidenceKey): EvidenceKeyTrustState {
  if (!key.trusted) return "announced_unconfirmed";
  if (!key.announced) return "confirmed_never_announced";
  if (key.vouched_by === "attestation") return "platform_vouched";
  if (key.vouched_by === "human") {
    // BEFORE the scope split, because it is the narrower fact and the more important one. An
    // override IS a tenant-scope, human confirmation — it would otherwise render as
    // "person_confirmed", which is true and misleading: the person worked for PureID and could not
    // walk to this machine. The tenant has to be able to see that from their own console without
    // reading an audit log, which is the entire reason the flag is recorded.
    if (key.is_platform_override) return "platform_override_confirmed";
    return key.scope === "edge" ? "provider_confirmed" : "person_confirmed";
  }
  return "confirmed_unattributed";
}

/**
 * Can THIS tenant withdraw this confirmation?
 *
 * READ FROM `scope`, never from the trust state. The two questions have different answers on the
 * same row: a platform-vouched key at tenant scope is one the tenant can take away, and a
 * person-confirmed key at edge scope is one they cannot. A surface that offered the button on
 * everything trusted would answer 409 on the rows that matter most.
 *
 * An announced-but-unconfirmed key has nothing to withdraw, so this is false for it too.
 */
export function tenantMayWithdrawTrust(key: EvidenceKey): boolean {
  return key.trusted && key.scope === "tenant";
}

/**
 * Counts announced signing identities, not live processes or observed traffic.
 * `landing` means confirmed; it does not establish that records have been received.
 */
export function instanceCounts(keys: readonly EvidenceKey[]): { landing: number; waiting: number } {
  let landing = 0;
  let waiting = 0;
  for (const key of keys) {
    if (!key.announced) continue;
    if (key.trusted) landing += 1;
    else waiting += 1;
  }
  return { landing, waiting };
}

/** The most recent reported timestamp among announced keys, not proof of access activity. */
export function lastSeen(keys: readonly EvidenceKey[]): string | null {
  let newest: string | null = null;
  for (const key of keys) {
    if (!key.announced || !key.last_seen_at) continue;
    if (newest === null || key.last_seen_at > newest) newest = key.last_seen_at;
  }
  return newest;
}

/**
 * The evidence-key thumbprint, grouped for a human to compare.
 *
 * GROUPED IN FOURS BECAUSE OF WHAT THE VALUE IS. An RFC 7638 thumbprint is 43 characters of
 * unpadded base64url, MIXED CASE, where `a` and `A` are different characters. 43 does not divide by
 * eight, so eights would leave a ragged 3-character group in a place the eye does not expect; fours
 * divide into ten groups and a 3-character tail, which reads as a deliberate ending rather than a
 * truncation.
 *
 * THE PERSON COMPARING THIS STRING IS THE CONTROL, which is why the rendering is load-bearing. The
 * control plane never sees this key's private half, so unlike every other value on this surface it
 * cannot check this one for itself. It shipped rendered raw, ungrouped, at 13px, in a horizontally
 * scrolling block.
 */
export function groupThumbprint(thumbprint: string): string {
  return (thumbprint.match(/.{1,4}/g) ?? [thumbprint]).join(" ");
}

// ── What this edge may do with your authority ──────────────────────────────
//
// Transcribed from backend/internal/edge/edgeplane/capability.go and the additive pair in
// backend/internal/edge/edgeenrollment/capability_verbs.go:
//
//   GET  /edge-deployments/{edge_id}/capabilities                     → { capabilities: [verb] }
//   POST /edge-deployments/{edge_id}/capabilities/{verb}/grant        → 204
//   POST /edge-deployments/{edge_id}/capabilities/{verb}/withdraw     → 204
//
// THE REPLACE ENDPOINT IS NOT USED HERE. `PUT /{edge_id}/capabilities` takes the COMPLETE set and
// deletes whatever it does not name, so a caller meaning "also allow this" withdraws everything
// else in the same statement and the edge stops pushing evidence hours before anyone notices. The
// two verbs above are one verb, one direction, idempotent, and no read-modify-write. This surface
// exists to be the thing operators reach for instead of that PUT.
//
// THE VOCABULARY IS TRANSCRIBED, NOT READ. The API returns only what is ALREADY delegated, and
// this panel has to say what COULD be as well, so the bundles below are a copy of capability.go's
// and there is no endpoint that would let the console discover them. Two consequences, both
// deliberate: a verb added to the plane is invisible here until this table names it (a verb this
// build does not know is rendered as itself if it comes back granted, never hidden), and the three
// EDGE-SCOPE verbs — config_pull, cert_renew, replay — are absent on purpose. They act on the
// machine rather than on the tenant's authority, they belong to whoever operates the edge, and the
// API refuses a tenant who names one. Offering them would be offering a button that answers 400.

/** How sharp a bundle is, which is what decides the weight of the confirmation in front of it. */
export type CapabilityWeight = "ordinary" | "authority" | "secret";

export interface CapabilityBundle {
  id: string;
  /** What the bundle is for, in the words of somebody deciding whether to delegate it. */
  title: string;
  purpose: string;
  weight: CapabilityWeight;
  verbs: readonly string[];
}

/**
 * What each verb lets the edge do, said to the person who has to decide whether to allow it.
 *
 * THE WIRE NAME IS NOT A LABEL. `credential_release` is the value in the config, the audit row and
 * the plane's refusal; it is not a thing a customer should have to decode to answer "may this
 * machine read my Stripe key?". So each verb is named twice, the same way the doors panel names a
 * door twice: the sentence is the label, and the raw verb rides beside it in the mono data voice
 * for the operator who has to match this screen against a log line. Neither one is dropped —
 * dropping the sentence makes the reader learn our vocabulary, dropping the mono name makes the
 * operator guess at the mapping.
 */
export const CAPABILITY_COPY: Record<string, { title: string; what: string }> = {
  evidence_push: {
    title: "Record what it decided",
    what: "Send PureID the signed record of every call this edge allowed or refused. Without it your audit trail has a gap wherever this edge decided.",
  },
  edge_key_announce: {
    title: "Publish its instances' signing keys",
    what: "Each running instance offers the key it signs those records with, so you can compare the fingerprint and let its evidence land.",
  },
  revocation_check: {
    title: "Check that an identity is still good",
    what: "Ask PureID whether the identity behind a call has been cut off, before the call is allowed.",
  },
  approval_find: {
    title: "Look up the approval a call needs",
    what: "Find the approval that covers a call, so one a person already granted is not asked for twice.",
  },
  approval_pending: {
    title: "Hold a call while a person decides",
    what: "Park a call that needs a human answer and raise the ask, rather than refusing it outright.",
  },
  approval_status: {
    title: "Check whether a person has answered",
    what: "Read whether an ask has been answered yet. It reads the answer and cannot spend it.",
  },
  session_open: {
    title: "Admit a person",
    what: "Let a person through this edge to a system inside your network, within what your policy already permits them.",
  },
  session_heartbeat: {
    title: "Keep an admitted person's session alive",
    what: "Renew a session that is already open, up to your tenant's session ceiling.",
  },
  session_close: {
    title: "End a session",
    what: "Close a session cleanly when the person is finished or when you cut it.",
  },
  session_adopt: {
    title: "Carry a session across a restart",
    what: "Let a restarted or additional instance of this edge take over sessions already open, so people are not signed out when a pod moves.",
  },
  relay_serve: {
    title: "Serve people outside your network through the relay",
    what: "Hold a pool of connections open to PureID's relay and answer the ones it splices through, so a person at home reaches this edge without a VPN. Your published application names point at the relay for exactly as long as this is allowed.",
  },
  lease_reserve: {
    title: "Hold money against a call",
    what: "Put a hold on your spending caps before a call runs, so a call that would exceed them never starts.",
  },
  lease_settle: {
    title: "Settle a hold at the real amount",
    what: "Close a hold at what the call actually cost, and return the difference to your caps.",
  },
  lease_refund: {
    title: "Return a hold that was not used",
    what: "Release a hold for a call that did not run. It only gives money back.",
  },
  charge: {
    title: "Charge without holding first",
    what: "Record a settled charge directly against your spending. Your caps are not checked, so an edge that holds it can spend past them.",
  },
  approval_consume: {
    title: "Spend a person's one-time approval",
    what: "Use up the single-use approval a person gave. Once spent it is gone, and this edge is what decides when.",
  },
  virtual_key_resolve: {
    title: "Turn a virtual key into a real grant",
    what: "Take a virtual key your agent presents and mint the grant it stands for. That is issuing authority, not checking it.",
  },
  forward_sign: {
    title: "Sign onward calls with your key",
    what: "Sign a forwarded call with your tenant's own key, so whatever receives it treats the call as carrying your authority.",
  },
  credential_release: {
    title: "Release your third-party credentials",
    what: "Request release of a third-party credential encrypted to the authorized agent’s registered key. Edge cannot decrypt it. The agent can use the released credential directly at the destination.",
  },
  agent_broker: {
    title: "Broker agent identity and delegation",
    what: "Relay agent lifecycle and delegation requests to PureID, where the applicable authority is checked.",
  },
};

/**
 * The bundles, ordered least sharp first, ending on the one that carries a secret.
 *
 * Mirrors capability.go's own grouping — Serve, PrivateAccess, Spend, Grant, Custody — because
 * that file already explains why each is separate, and a console that regrouped them would be
 * asserting a second, quieter policy beside the one that is enforced.
 */
export const CAPABILITY_BUNDLES: readonly CapabilityBundle[] = [
  {
    id: "serve",
    title: "Front your traffic and report what it decided",
    purpose:
      "What every enrolled edge is given. An edge with only these can decide calls and can move nothing of yours.",
    weight: "ordinary",
    verbs: [
      "evidence_push",
      "edge_key_announce",
      "revocation_check",
      "approval_find",
      "approval_pending",
      "approval_status",
    ],
  },
  {
    id: "private_access",
    title: "Admit people to your internal systems",
    purpose:
      "The vocabulary behind the private door. It admits a person to what your policy already permits them and moves no money.",
    weight: "ordinary",
    verbs: ["session_open", "session_heartbeat", "session_close", "session_adopt"],
  },
  {
    id: "relay_serve",
    title: "Reach your internal systems from outside your network",
    purpose:
      "Lets this edge hold a connection pool open to PureID's relay so a person at home reaches the same private door. While allowed, your published application names point at the relay; PureID splices the connection by its server name and never decrypts it.",
    weight: "ordinary",
    verbs: ["relay_serve"],
  },
  {
    id: "spend",
    title: "Spend against your budgets",
    purpose:
      "These move your money. An edge that only proxies reads never needs any of them.",
    weight: "authority",
    verbs: ["lease_reserve", "lease_settle", "lease_refund", "charge"],
  },
  {
    id: "issue",
    title: "Issue authority in your name",
    purpose:
      "Each of these turns this edge from something that enforces your rules into something that hands out authority under them.",
    weight: "authority",
    verbs: ["approval_consume", "virtual_key_resolve", "forward_sign"],
  },
  {
    id: "agent_broker",
    title: "Agent identity and delegation",
    purpose: "The agent lifecycle permission included with an API governance installation.",
    weight: "authority",
    verbs: ["agent_broker"],
  },
  {
    id: "custody",
    title: "Release third-party credentials to agents",
    purpose:
      "An outbound Edge can request encrypted credential release to authorized agents. This requires an approved manifest naming credential release and two distinct administrator signatures.",
    weight: "secret",
    verbs: ["credential_release"],
  },
];

/** The verb the custody bundle is made of, named once so the sharp path is not string-matched twice. */
export const CUSTODY_VERB = "credential_release";

/**
 * What the relay's publisher last did for this tenant's public name.
 *
 * IT TRAVELS WITH THE SET BECAUSE THE SET ALONE LIES. `relay_serve` in the delegated list says the
 * capability is held; it does not say the `*.<tenant>.<application domain>` name that delegation publishes
 * actually reached the zone. When a DNS write fails the plane records it here rather than swallowing
 * it, so the operator reads the divergence on this page instead of from a remote user who cannot
 * connect. Null until a grant has ever attempted it.
 */
export const relayPublicationSchema = z.object({
  name: z.string(),
  target: z.string(),
  state: z.enum(["pending", "published", "failed", "withdrawn"]),
  caa_issuer: z.string().optional(),
  caa_account: z.string().optional(),
  failure: z.string().optional(),
  attempted_at: z.string(),
  updated_at: z.string(),
});
export type RelayPublication = z.infer<typeof relayPublicationSchema>;

export const edgeCapabilitiesResponseSchema = z.object({
  capabilities: z.array(z.string()),
  relay_name: relayPublicationSchema.nullish(),
});
export type EdgeCapabilitiesResponse = z.infer<typeof edgeCapabilitiesResponseSchema>;

export interface CapabilityRow {
  verb: string;
  title: string;
  what: string;
  delegated: boolean;
}

export interface CapabilityGroupView extends Omit<CapabilityBundle, "verbs"> {
  rows: CapabilityRow[];
  delegated: number;
}

/**
 * Fold what is delegated into the vocabulary, in one place.
 *
 * `unlisted` is every granted name this build does not know. It is rendered rather than dropped:
 * a verb the plane added after this bundle shipped is still authority this edge holds, and a
 * census that quietly omitted it would be answering "what may this machine do" with a subset while
 * looking complete. It carries no buttons — this console cannot describe what it would be taking
 * away.
 */
export function groupCapabilities(granted: readonly string[]): {
  groups: CapabilityGroupView[];
  unlisted: string[];
} {
  const held = new Set(granted);
  const known = new Set<string>();
  const groups = CAPABILITY_BUNDLES.map(({ verbs, ...bundle }) => {
    const rows = verbs.map((verb) => {
      known.add(verb);
      const copy = CAPABILITY_COPY[verb];
      return {
        verb,
        title: copy?.title ?? verb,
        what: copy?.what ?? "",
        delegated: held.has(verb),
      };
    });
    return { ...bundle, rows, delegated: rows.filter((r) => r.delegated).length };
  });
  const unlisted = granted.filter((verb) => !known.has(verb)).sort();
  return { groups, unlisted };
}

/**
 * Whether withdrawing this verb would empty the set.
 *
 * The server refuses it and says why, and this is the same rule computed locally so the button is
 * not offered in the first place. It is NOT a substitute for surfacing the refusal: another admin
 * can withdraw the second-to-last verb between this render and the click, and the server's sentence
 * is what explains that.
 */
export function wouldEmptyDelegation(granted: readonly string[], verb: string): boolean {
  return granted.length === 1 && granted[0] === verb;
}

/**
 * The sentence behind a 400, made fit to show an operator.
 *
 * THE ENDPOINT ANSWERS IN THE HOUSE SHAPE, so `error.message` already carries the sentence. It did
 * not always: the handler briefly sent `{"error":"invalid_capability","detail":"<sentence>"}`, and
 * the api client resolves a message by trying "error", "message", "detail", "title" IN THAT ORDER —
 * so a payload carrying both returned the code and dropped the only thing that explained the
 * refusal. That was fixed at the source (xhttp.WriteError separates the machine-readable code from
 * the human one), which is why this reads the message rather than reaching into the body.
 *
 * WHAT IT STILL DOES is trim the Go error chain's package prefix and nothing else. `edgeenrollment:
 * invalid input:` is not a sentence and names a package a tenant has never heard of; what follows it
 * was written to be read by the operator and is passed through exactly as sent. The two refusals it
 * carries are "that verb is not yours to delegate" and "that is the last one, and an edge that may
 * do nothing is refused at compile" — neither is reconstructible from the code alone.
 */
export function capabilityRefusalDetail(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return null;
  const trimmed = message.replace(/^(?:[a-z][a-z0-9_]*:\s*)?invalid input:\s*/i, "").trim();
  return trimmed === "" ? null : trimmed;
}


/**
 * The weight of the bundle a verb belongs to — what decides how heavy the confirmation in front of
 * it is. An unknown verb is "ordinary" rather than throwing: nothing on this surface offers an act
 * for a verb it cannot place, so the value is only ever read for a row the table named.
 */
export function capabilityWeight(verb: string): CapabilityWeight {
  for (const bundle of CAPABILITY_BUNDLES) {
    if (bundle.verbs.includes(verb)) return bundle.weight;
  }
  return "ordinary";
}

// ── Containment: the tenant's own stop button, and the way back ────────────
//
// Transcribed from backend/internal/edge/edgeenrollment/containment_handler.go:
//
//   POST /edge-deployments/{edge_id}/revoke      { reason } → 204
//   POST /edge-deployments/{edge_id}/restore     { reason } → 204
//   GET  /edge-deployments/{edge_id}/containment           → 200 { events: [...] }
//
// THE REASON IS REQUIRED IN BOTH DIRECTIONS and the server enforces it, so the forms do too. Revoking
// is the act that answers "why is this edge dead"; restoring is the act that ends an incident, and
// "why did we decide it was safe" is what the next reviewer reads.
//
// THE HISTORY IS APPEND-ONLY AND SURVIVES A RESTORE. `RestoreEdge` clears `revoked_at` and
// `revoked_reason` because those two columns are CURRENT STATE; the record lives in
// `edge_containment_event`, which is why this surface reads that endpoint rather than the roster
// row. An edge cut off in March and running today still shows the March act and the reason for it.

/**
 * How long the edge's own cached configuration can outlive the control plane's refusal, in minutes.
 *
 * THIS NUMBER IS WHY THE COPY ON THIS SURFACE IS NOT "~15 SECONDS". Revocation lands at the control
 * plane's authorization layer at the next registry reload, and that genuinely is about fifteen
 * seconds — but the edge keeps DECIDING from the configuration it already holds until that copy
 * expires. `edgemount.DefaultConfigTTL` is 30 minutes, and an operator who set
 * `PUREID_EDGE_CONFIG_MAX_STALE_SECONDS` has bought themselves more on top of it (the default is
 * zero — strict). Printing the first clock as if it were the second one would tell a customer their
 * compromised machine had stopped when it had not.
 */
export const CONFIG_CACHE_MINUTES = 30;

export const containmentEventSchema = z.object({
  /**
   * `revoked` | `restored`. Read as a string rather than an enum on purpose: the history is the
   * edge's RECORD, and a row this console cannot name must still render with its reason, its
   * timestamp and its actor rather than failing the whole read.
   */
  act: z.string(),
  reason: z.string(),
  /**
   * Absent for the provider-CLI path, which has no `admin_users` row in the tenant's scope. The
   * backend records none rather than inventing one, so the surface says "PureID" and stops there.
   */
  actor_admin_id: z.string().optional(),
  /** `tenant` | `platform`. Who this was done on the authority of, which is not the same as why. */
  actor_scope: z.string(),
  actor_session_type: z.string().optional(),
  at: z.string(),
});
export type ContainmentEvent = z.infer<typeof containmentEventSchema>;

export const containmentHistoryResponseSchema = z.object({
  events: z.array(containmentEventSchema),
});
export type ContainmentHistoryResponse = z.infer<typeof containmentHistoryResponseSchema>;

/**
 * Newest first, which is the order the server already answers in (`ORDER BY at DESC, id DESC`).
 *
 * Re-sorted here anyway, and stably: the tie-break column `id` is not on the wire, so equal
 * timestamps keep the server's ordering, and a future endpoint that forgot the ORDER BY cannot
 * make this panel claim that a restore came before the revocation it undid.
 */
export function orderContainment(
  events: readonly ContainmentEvent[],
): readonly ContainmentEvent[] {
  return [...events]
    .map((event, index) => ({ event, index }))
    .sort((a, b) => {
      const at = Date.parse(b.event.at) - Date.parse(a.event.at);
      return Number.isNaN(at) || at === 0 ? a.index - b.index : at;
    })
    .map(({ event }) => event);
}

/** The most recent act of one kind, or nothing. Used to lead a cut-off edge with its own reason. */
export function latestContainment(
  events: readonly ContainmentEvent[],
  act: string,
): ContainmentEvent | undefined {
  return orderContainment(events).find((event) => event.act === act);
}

/** What one act is called on screen. Sentence case, and never the wire word on its own. */
export function containmentActLabel(act: string): string {
  if (act === "revoked") return "Cut off";
  if (act === "restored") return "Restored";
  if (act === "contained") return "Contained";
  return act;
}

/** Whether the record says the edge was contained: never lifted, so the latest act is not the test. */
export function isContained(events: readonly ContainmentEvent[] | undefined): boolean {
  return !!events?.some((event) => event.act === "contained");
}

/**
 * Whose authority the act was taken on.
 *
 * `tenant` is the ordinary case now that containment is reachable from this console, and it covers
 * a delegated MSSP administrator too — a grant makes them a tenant-scope admin in this tenant, so
 * their acts are the customer's acts. `platform` means PureID did it, which before 2026-08-24 was
 * the only way it could happen at all.
 */
export function containmentScopeLabel(scope: string): string {
  if (scope === "tenant") return "Your organization";
  if (scope === "platform") return "PureID";
  return scope;
}

/**
 * What the server said when it refused a containment change.
 *
 * THE TWO CONFLICTS ARE NOT FAILURES THE OPERATOR MUST FIX. `already_revoked` means containment
 * already holds, which is what they were trying to achieve; the first revocation's timestamp and
 * reason are deliberately left alone so the incident's record survives. `not_revoked` means
 * somebody restored it while this dialog was open. Both are answers, and both close the dialog.
 *
 * The 400 is the opposite: it carries a full sentence naming the rule that was broken, and it is
 * shown verbatim in a banner the dialog holds open.
 */
export type ContainmentRefusal =
  | { kind: "already_revoked" }
  | { kind: "not_revoked" }
  | { kind: "already_contained" }
  | { kind: "contained" }
  | { kind: "file_registry" }
  | { kind: "not_found" }
  | { kind: "sentence"; sentence: string };

/**
 * The stable code the handler sent, read from the response body first.
 *
 * The body is `{"error":"already_revoked"}` and the api client's message extraction already prefers
 * that key, so the message would carry it too — but the body is where the contract lives, and a
 * later change to the client's fallback order must not turn a conflict into an unrecognised string.
 *
 * Shared by every refusal on this surface rather than copied per panel: the edge endpoints all
 * refuse in the same house shape, and two readers of one shape drift.
 */
export function edgeRefusalCode(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  const body = (error as { body?: unknown }).body;
  if (body && typeof body === "object") {
    const value = (body as Record<string, unknown>).error;
    if (typeof value === "string") return value.trim();
  }
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

export function readContainmentRefusal(error: unknown): ContainmentRefusal {
  switch (edgeRefusalCode(error)) {
    case "already_revoked":
      return { kind: "already_revoked" };
    case "not_revoked":
      return { kind: "not_revoked" };
    case "already_contained":
      return { kind: "already_contained" };
    case "contained":
      return { kind: "contained" };
    case "file_registry":
      return { kind: "file_registry" };
    case "not_found":
      return { kind: "not_found" };
    default:
      break;
  }
  // The 400's `detail` is a whole sentence; `capabilityRefusalDetail` strips only the Go package
  // prefix in front of it, which names a package a tenant has never heard of. It is shared with the
  // capability surface because both endpoints refuse in the same house shape, for the same reason.
  const sentence = capabilityRefusalDetail(error);
  return {
    kind: "sentence",
    sentence:
      sentence ??
      (error instanceof Error && error.message.trim() !== ""
        ? error.message
        : "The change did not take effect."),
  };
}

// ── The attestation binding: the standing grant of an edge's identity to a workload class ──
//
// Transcribed from backend/internal/edge/edgeenrollment/attestation_handler.go:
//
//   GET    /edge-deployments/{id}/attestation → attestationState              (edge.read)
//   PUT    /edge-deployments/{id}/attestation → attestationState              (edge.manage, step-up,
//                                                                              interactive session)
//   DELETE /edge-deployments/{id}/attestation → attestationState              (edge.manage, step-up)
//
// WHAT IT IS, because the name says nothing to a reader who does not already know: a binding says
// "a workload that can present a platform token from this issuer, for this audience, naming this
// subject, IS this edge deployment". Anything satisfying it can obtain the edge's certificate
// without holding one, and can have its evidence-signing key trusted with no person comparing a
// fingerprint. Without one there is no automated recovery: an edge that misses its renewal window
// is re-enrolled by a person.
//
// THE RESPONSE CARRIES NO PEM, DELIBERATELY. Keys come back as SHA-256 fingerprints — the form an
// operator can compare against their own cluster, and the form the audit row carries. The console
// must never present a fingerprint as if it were the key.

export const attestationBindingSchema = z.object({
  method: z.string(),
  issuer: z.string(),
  audience: z.string(),
  subject: z.string(),
  /** SHA-256 over each pinned key's DER bytes, sorted by the server. Never the key itself. */
  signing_key_fingerprints: z.array(z.string()),
  jwks_url: z.string().optional(),
  jwks_trust_fingerprint: z.string().optional(),
});
export type AttestationBinding = z.infer<typeof attestationBindingSchema>;

export const attestationStateSchema = z.object({
  edge_id: z.string(),
  /** Who RUNS this edge deployment. The first place this tenant-visible field appears at all. */
  operator: z.string(),
  /**
   * Whether this tenant may change the binding, ANSWERED BY THE SERVER. Never re-derived here from
   * `operator`: the rule is the server's and a local copy would eventually offer a button that
   * answers 409.
   */
  tenant_writable: z.boolean(),
  /** Absent when nothing is pinned — a real and common state, not an error. */
  binding: attestationBindingSchema.optional(),
  /** The canonical one-line rendering; the same string the audit row carries. */
  summary: z.string().optional(),
  /** What this state means for recovery, in the server's own words. Shown, never paraphrased. */
  detail: z.string(),
  /** A credential has been issued before, so the re-issuance posture applies to this edge. */
  has_enrolment_key: z.boolean(),
  /** A re-issuance on attestation is refused unless a recovery window is open. */
  reissuance_requires_admin: z.boolean(),
  /** The end of the open recovery window (RFC 3339), absent when none is open. */
  reissuance_window_until: z.string().optional(),
});
export type AttestationState = z.infer<typeof attestationStateSchema>;

/**
 * The form a person fills in to pin one.
 *
 * `signing_keys_pem` is a textarea rather than a file picker because the value an operator has to
 * hand is what `kubectl get --raw /openid/v1/jwks` or their cluster's documentation prints. It is
 * PUBLIC key material; a private key is refused by the server by name, and this form says so before
 * anybody pastes one.
 *
 * NO `.default()` ANYWHERE — defaults belong in defaultValues, not in a resolver schema.
 */
/**
 * The workload-class fields, shared by the two forms that state one.
 *
 * ONE DEFINITION, TWO FORMS. Pinning a class onto an existing edge and authorising a new edge WITH
 * its class are the same statement in two places, and the rules attached to it — no namespace
 * `default` service account, no private key, discovery must pin its CA — are the whole reason the
 * statement is safe. Two copies would be two places for those to drift, and the direction they drift
 * in is toward the laxer one.
 */
const attestationBindingFields = {
  /**
   * ONE IMPLEMENTED METHOD. It is a closed set rather than free text because the server refuses a
   * name with nothing behind it, and offering a choice that is always refused is a surface that
   * appears to answer. When a second method is implemented it lands here and in the server's
   * list together.
   */
  method: z.literal("k8s_sat"),
  issuer: z.string().trim(),
  audience: z.string().trim(),
  subject: z
    .string()
    .trim()
    .refine((s) => !/^system:serviceaccount:[^:]+:default$/.test(s), {
      message:
        "That pins the namespace's default service account, so every pod in the namespace running as default could act as this edge deployment. Give the edge deployment a service account of its own and pin that.",
    }),
  signing_keys_pem: z.string(),
  jwks_url: z.string().trim(),
  jwks_tls_trust_pem: z.string(),
};

/** The cross-field rules on a workload class, applied by both forms that state one. */
function refineAttestationBinding(
  v: {
    issuer: string;
    audience: string;
    subject: string;
    signing_keys_pem: string;
    jwks_url: string;
    jwks_tls_trust_pem: string;
  },
  ctx: z.RefinementCtx,
) {
  for (const [field, message] of [
    ["issuer", "The cluster's OIDC issuer is required"],
    ["audience", "The audience the token must be minted for is required"],
    ["subject", "The workload the token must name is required"],
  ] as const) {
    if (v[field].trim() === "") ctx.addIssue({ code: "custom", path: [field], message });
  }
  // A PRIVATE KEY IS CAUGHT AT THE KEYBOARD. The server refuses it too and stores nothing — this
  // is here so the person who reached for the wrong file next to the certificate finds out before
  // the secret leaves their browser.
  if (v.signing_keys_pem.includes("PRIVATE KEY")) {
    ctx.addIssue({
      code: "custom",
      path: ["signing_keys_pem"],
      message:
        "That is a private key. A binding pins the platform's public token-signing keys, which are published. Nothing was sent.",
    });
  }
  if (v.jwks_tls_trust_pem.includes("PRIVATE KEY")) {
    ctx.addIssue({
      code: "custom",
      path: ["jwks_tls_trust_pem"],
      message: "That is a private key. This field pins the CA certificate the key-set fetch trusts. Nothing was sent.",
    });
  }
  // DISCOVERY MUST PIN: a URL with no trust bundle would fetch on whatever the network answers,
  // and a trust bundle with no URL is an operator believing discovery is on when it is not. The
  // server refuses each half; saying so here keeps the round trip out of the loop.
  if (v.jwks_url !== "" && v.jwks_tls_trust_pem.trim() === "") {
    ctx.addIssue({
      code: "custom",
      path: ["jwks_tls_trust_pem"],
      message: "A key-set URL must pin the CA that authenticates the fetch, or the trust root of the recovery path is whatever the network answers.",
    });
  }
  if (v.jwks_url === "" && v.jwks_tls_trust_pem.trim() !== "") {
    ctx.addIssue({
      code: "custom",
      path: ["jwks_url"],
      message: "A pinned CA with no key-set URL is a fetch that never happens.",
    });
  }
  if (v.jwks_url !== "" && !v.jwks_url.startsWith("https://")) {
    ctx.addIssue({
      code: "custom",
      path: ["jwks_url"],
      message: "The key-set URL must be https — it decides authentication.",
    });
  }
  if (v.signing_keys_pem.trim() === "" && v.jwks_url === "") {
    ctx.addIssue({
      code: "custom",
      path: ["signing_keys_pem"],
      message: "Pin at least one signing key, or name a key-set URL. Otherwise nothing could verify a token.",
    });
  }
}

export const pinAttestationFormSchema = z
  .object(attestationBindingFields)
  .superRefine(refineAttestationBinding);
export type PinAttestationForm = z.infer<typeof pinAttestationFormSchema>;

/**
 * AUTHORISE AN EDGE THAT DOES NOT EXIST YET — the one human act in a Kubernetes enrolment.
 *
 * An administrator states the name, what it may front, who runs it, and the workload class allowed
 * to become it. Then they are finished: the operator installs whenever their change window falls,
 * the pod proves what it is with the identity its own cluster issued, and nobody waits for anybody.
 *
 * The edge id rules match the server's, which are narrower than they look and for a reason worth
 * knowing at the keyboard: an id is a deployment-wide primary key that is NEVER FREED, so a typo is
 * a name nobody can reuse.
 */
export const authoriseEdgeFormSchema = z
  .object({
    edge_id: z
      .string()
      .trim()
      .min(1, "A name is required")
      .max(64, "A name is at most 64 characters")
      .regex(
        /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
        "Lower-case letters, digits and hyphens, starting and ending with a letter or digit",
      ),
    /**
     * At least one door, because an edge fronting nothing is a row that looks like progress. The
     * server normalises and refuses the same vocabulary; this keeps the round trip out of the loop.
     */
    role: z.enum(["broker", "web", "egress", "privileged"]),
    /**
     * ONLY `customer`. A provider-run edge and the control plane are the same party however well the
     * keys are separated, so a standing grant to become one is PureID vouching for PureID — the
     * server refuses it and the database makes the trust row unrepresentable. A literal rather than
     * a choice, because offering an option that is always refused is a surface that appears to
     * answer.
     */
    operator: z.literal("customer"),
    /**
     * THE DEPLOYMENT THIS EDGE JOINS, and who else is in it.
     *
     * A DEPLOYMENT IS A SET OF EDGES THAT COME AND GO TOGETHER — Atlas's two doors are one
     * deployment of two. Leaving both blank authorises a deployment of ONE, which is the literal
     * truth of authorising a single edge and is why the common case stays a one-screen act.
     *
     * IT IS A CONTROL, NOT A LABEL. Before any of them can register its inventory, every member
     * must have been authorised naming the SAME member set — which is what stops one machine
     * claiming to belong beside machines that never agreed to it. So a multi-edge deployment means
     * typing the same list on each member's authorisation, deliberately: the repetition IS the
     * agreement. The server refuses a manifest that contradicts one already standing, because a
     * claim cannot be edited.
     */
    deployment_id: z
      .string()
      .trim()
      .max(63, "A deployment name is at most 63 characters")
      .regex(
        /^[a-z0-9][a-z0-9-]*$/,
        "Lower-case letters, digits and hyphens, starting with a letter or digit",
      )
      .or(z.literal("")),
    deployment_edge_ids: z.array(z.string().trim().min(1)).max(32, "A deployment holds at most 32 edges"),
    ...attestationBindingFields,
  })
  .superRefine((v, ctx) => {
    // A runtime that admits people enrols by a person comparing a key fingerprint and carries no workload class.
    if (!admitsPeople(v.role)) refineAttestationBinding(v, ctx);
  })
  .superRefine((v, ctx) => {
    // THE EDGE MUST BE IN ITS OWN MANIFEST. The server refuses this too, and says so clearly — but
    // it is the typo an administrator makes while listing siblings, and a round trip to learn it is
    // a round trip inside a step-up window.
    if (v.deployment_edge_ids.length > 0 && !v.deployment_edge_ids.includes(v.edge_id)) {
      ctx.addIssue({
        code: "custom",
        path: ["deployment_edge_ids"],
        message: "The member list has to include this edge",
      });
    }
  });
export type AuthoriseEdgeForm = z.infer<typeof authoriseEdgeFormSchema>;

/** What the server answers when an edge has been authorised. */
export const authoriseEdgeResponseSchema = z.object({
  edge_id: z.string(),
  spiffe_id: z.string(),
  operator: z.string(),
  next: z.string(),
});
export type AuthoriseEdgeResponse = z.infer<typeof authoriseEdgeResponseSchema>;


/**
 * Split a pasted bundle into PEM blocks.
 *
 * The wire field is an ARRAY of keys and the textarea is one paste, which is how an operator holds
 * the value. Splitting here rather than in the component keeps the wire shape's definition in one
 * place — and an unsplit paste would arrive as a single "key" the server cannot parse.
 */
export function splitPEMBlocks(text: string): string[] {
  const matches = text.match(/-----BEGIN [A-Z0-9 ]+-----[\s\S]*?-----END [A-Z0-9 ]+-----/g);
  return matches ?? [];
}

// A SITE IS ONE CLUSTER AT ONE LOCATION, and the unit an installation is prepared at. The edge id
// is composed from the tenant's slug, the site's name and the runtime's, so both are named here
// rather than generated — an administrator reading an edge id learns where it runs and what it is.
export type EdgeSite = z.infer<typeof edgeSiteSchema>;
export const edgeSitesSchema = z.object({ sites: z.array(edgeSiteSchema) });

// The runtime and site name shapes are the server's (chk_edge_runtime_name_shape) and the message
// says what the value BECOMES, because that is why the shape is narrow.
const nameShape = /^[a-z0-9][a-z0-9-]{0,62}$/;
const nameMessage = "Lowercase letters, digits and hyphens — it becomes part of this Edge's identity and its certificate";

export const createEdgeSiteSchema = z.object({
  name: z.string().trim().regex(nameShape, nameMessage),
  // The cluster's OIDC issuer. It is the site's identity, so no other tenant may pin the same one.
  issuer: z.string().trim().url("Enter the cluster's issuer URL")
    .refine((v) => v.startsWith("https://"), "The issuer must be an https URL — it is a trust anchor, not an address"),
});
export type CreateEdgeSiteForm = z.infer<typeof createEdgeSiteSchema>;

export const prepareEdgeSchema = z.object({
  // All three doors are preparable. The internet door was missing here while the CONNECT proxy it
  // serves already existed everywhere else — chart, plane and vocabulary — so the only thing an
  // administrator could not do was bring one into being.
  role: z.enum(["broker", "web", "egress", "privileged"]),
  site_id: z.string().uuid("Choose the site this Edge runs at"),
  runtime_name: z.string().trim().regex(nameShape, nameMessage),
});
export type PrepareEdgeForm = z.infer<typeof prepareEdgeSchema>;
export const edgeRegistrationsSchema = z.object({ registrations: z.array(edgeRegistrationSchema) });
export type EdgeRegistration = z.infer<typeof edgeRegistrationSchema>;
export type EdgeRegistrationProposal = z.infer<typeof edgeRegistrationProposalSchema>;
export type EdgeRegistrationDecision = {
  intent_id: string; act: "approve" | "reject" | "cancel"; proposal_id?: string; digest?: string;
  enrollment_code?: string;
};

/** Do not mistake traffic authority or absent health data for installation completion. */
export function registrationNeedsAttention(registration: EdgeRegistration, deployments: readonly EdgeDeployment[]) {
  const deployment = deployments.find((edge) => edge.edge_id === registration.edge_id);
  if (deployment && hasEdgeRetirement(deployment)) return false;
  if (registration.state === "pending") return true;
  if (registration.state !== "authorised") return false;
  if (!registration.approval_current) return false;
  const installation = deployment?.installation;
  return !installation || (installation.state !== "revoked" &&
    !(installation.state === "active" && installation.connection === "connected"));
}

/** A connected installation still needs to expose its passkey-gated software-update handoff. */
export function registrationOffersImageUpdate(registration: EdgeRegistration) {
  return registration.state === "authorised" && registration.approval_current &&
    registration.image_update_supported && !!registration.approval_id;
}
